<template>
  <div
    :class="{ 'no-padding': !hasMediaContent, 'keyboard-open': isKeyboardOpen }"
    class="user-input"
  >
    <default-overlay />
    <qr-code-component
      v-if="!showThankYou"
      :qr-code-data="currentContent?.attrs?.qrCode"
      has-margin
    />
    <media-text
      v-if="headline.value?.trim().length && !showThankYou"
      :data="headline"
      :class="{ 'text-margin': !text.value }"
      class="headline"
      weight="bold"
    />
    <media-text
      v-if="text.value?.trim().length && !showThankYou"
      :data="text"
      class="text"
    />
    <div
      class="keyboard-wrapper"
      :class="{ 'shifted': isShifted, 'locked': isLocked }"
    >
      <div
        v-if="!showThankYou"
        :class="{
          'dropdown-input': isDropdown,
          'canvas-input': inputMode === 'canvas',
        }"
        class="input-block"
      >
        <div
          ref="keyboardRef"
          class="keyboard"
        />
        <canvas-input
          v-if="inputMode === 'canvas'"
          :canvas-data="inputValue"
          @data="onCanvasData"
          @drawing="restartNoInteraction"
          @clear="handleClear"
          @next="handleNext"
        />
        <dropdown-input
          v-else-if="isDropdown"
          :value="inputValue"
          :options="dropdownOptions"
          :placeholder="placeholder.value"
          @select="onDropdownData"
          @open="stopNoInteraction"
          @close="restartNoInteraction"
        />
        <div
          v-else
          class="input-wrapper"
        >
          <div
            :class="{
              empty: !inputValue.trim().length,
            }"
            class="input-holder"
          >
            <div class="fake-input">
              {{ fakeInputValue }}
            </div>
            <input
              ref="focusRef"
              v-if="inputMask"
              :value="inputValue"
              :placeholder="placeholder.value"
              :data-maska="inputMask"
              v-maska
              pattern="[0-9]*"
              class="input"
              @blur="handleBlur"
              @click="handleClick"
              @maska="handleMaskedInput"
              @keydown.enter.prevent="handleNext"
              @keydown.backspace="handleBackspace"
            >
            <textarea
              ref="focusRef"
              v-else
              v-model="inputValue"
              :placeholder="placeholder.value"
              :inputmode="inputMode"
              :maxlength="maxlength"
              class="input"
              @blur="handleBlur"
              @click="handleClick"
              @input="handleInput"
              @keydown.enter.prevent="handleNext"
              @keydown.backspace="handleBackspace"
            />
          </div>
          <div class="input-icons">
            <div
              v-if="showMicButton && !inputValue.trim().length"
              class="mic-icon icon"
              @click="activateStt"
            >
              <img src="icons/mic_grey.svg">
            </div>
            <div
              v-if="!inputValue.trim().length && inputMode !== 'canvas'"
              class="next-icon disabled icon"
            >
              <img src="@/assets/play_arrow.svg">
            </div>
          </div>
        </div>
        <div
          v-if="isDropdown || (inputValue.trim().length && inputMode !== 'canvas')"
          :class="{
            'no-borders': isDropdown,
          }"
          class="controls"
        >
          <div
            v-if="error"
            class="errors"
          >
            {{ error }}
          </div>
          <div
            v-if="inputValue.trim().length"
            class="clear-icon icon"
            @click="handleClear"
          >
            <img src="@/assets/close_gray.svg">
          </div>
          <div
            v-if="showMicButton && isDropdown"
            class="mic-icon icon margin-left"
            @click="activateStt"
          >
            <img src="icons/mic_grey.svg">
          </div>
          <progress-circle
            :value="progress"
            :class="{
              disabled: error || !inputValue.trim().length,
            }"
            stroke-color="#018ACF"
            class="progress-circle next-icon icon margin-left"
            @mousedown="handleNext"
          >
            <img src="@/assets/play_arrow.svg">
          </progress-circle>
        </div>
      </div>
    </div>

    <thank-you-text
      v-if="currentContent.attrs.endText.value && showThankYou"
    />
  </div>
</template>

<script>
import {
  computed,
  defineComponent,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import dayjs from 'dayjs';
import Keyboard from 'simple-keyboard';
import keyboardInputMask from 'simple-keyboard-input-mask';
import 'simple-keyboard/build/css/index.css';
import { useStt, ShortCodeService } from '@ht/lib-plural-vue';
import useSequence from '@/composables/useSequence';
import useContent from '@/composables/useContent';
import useConfig from '@/composables/useConfig';
import useActions from '@/composables/useActions';
import useAnalytics from '@/composables/useAnalytics';
import useDynamicText from '@/composables/useDynamicText';
import useCanvasInput from '@/composables/useCanvasInput';
import DefaultOverlay from '@/components/DefaultOverlay';
import QrCodeComponent from '@/components/QrCodeComponent';
import MediaText from '@/components/MediaText';
import ThankYouText from '@/components/ThankYouText';
import CanvasInput from '@/components/elements/user-input/CanvasInput.vue';
import DropdownInput from '@/components/elements/user-input/DropdownInput.vue';
import ProgressCircle from '@/components/ProgressCircle.vue';
import { SYSTEM_VARIABLES_MAP, INTERACTION_DELAY, VARIABLE_VALUE_TYPES, USER_INPUT_DEFAULT_PLACEHOLDERS } from '@/js/constants';
import types from '@/actions/types';
import userAgent from '@/js/userAgent';

const phoneRegExp = /^\+\d{10,14}$/;
const numbersRegExp = /^\d{1,}$/;
const specialCharactersRegExp = /[±§"$()*+;<>[\]\\^`{}~%]/m;
const emailRegExp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line
const timeRegexp = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
const dateRegexp = /^([1-9]|0[1-9]|[12][0-9]|3[01])[-.]([1-9]|0[1-9]|1[012])[-.](19|20)\d\d$/;
const VALIDATIONS = {
  number: (value) => (!numbersRegExp.test(value) ? 'Only numbers are allowed' : ''),
  email: (value) => (!emailRegExp.test(value) ? 'Email is not valid' : ''),
  phone: (value) => (!phoneRegExp.test(value) ? 'Phone number should start with country code f.e. +49' : ''),
  text: (value) => (specialCharactersRegExp.test(value) ? 'Special characters are not allowed' : ''),
  time: (value) => (!timeRegexp.test(value) ? 'Time is not valid' : ''),
  date: (value) => (!dateRegexp.test(value) ? 'Date not valid. Range 01.01.1900 - 31.12.2099' : ''),
  timeDate: (value) => {
    const [time, date] = value.split('|').map((v) => v.trim());
    if (!timeRegexp.test(time) || !dateRegexp.test(date)) {
      return 'Time and Date value is not valid';
    }

    return '';
  },
  canvas: () => '',
  dropdown: () => '',
};
const KEYBOARD_LAYOUTS = {
  de: () => import('simple-keyboard-layouts/build/layouts/german'),
  fr: () => import('simple-keyboard-layouts/build/layouts/french'),
  es: () => import('simple-keyboard-layouts/build/layouts/spanish'),
  ar: () => import('simple-keyboard-layouts/build/layouts/arabic'),
  'pt-BR': () => import('simple-keyboard-layouts/build/layouts/brazilian'),
  'zh-CN': () => import('simple-keyboard-layouts/build/layouts/chinese'),
  cs: () => import('simple-keyboard-layouts/build/layouts/czech'),
  el: () => import('simple-keyboard-layouts/build/layouts/greek'),
  he: () => import('simple-keyboard-layouts/build/layouts/hebrew'),
  hi: () => import('simple-keyboard-layouts/build/layouts/hindi'),
  hu: () => import('simple-keyboard-layouts/build/layouts/hungarian'),
  it: () => import('simple-keyboard-layouts/build/layouts/italian'),
  ja: () => import('simple-keyboard-layouts/build/layouts/japanese'),
  ko: () => import('simple-keyboard-layouts/build/layouts/korean'),
  ms: () => import('simple-keyboard-layouts/build/layouts/malayalam'),
  'es-419': () => import('simple-keyboard-layouts/build/layouts/spanish'),
  no: () => import('simple-keyboard-layouts/build/layouts/norwegian'),
  pl: () => import('simple-keyboard-layouts/build/layouts/polish'),
  pt: () => import('simple-keyboard-layouts/build/layouts/brazilian'),
  ru: () => import('simple-keyboard-layouts/build/layouts/russian'),
  sv: () => import('simple-keyboard-layouts/build/layouts/swedish'),
  th: () => import('simple-keyboard-layouts/build/layouts/thai'),
  tr: () => import('simple-keyboard-layouts/build/layouts/turkish'),
  uk: () => import('simple-keyboard-layouts/build/layouts/ukrainian'),
};

export default defineComponent({
  name: 'UserInputComponent',

  components: {
    DefaultOverlay,
    QrCodeComponent,
    ThankYouText,
    MediaText,
    CanvasInput,
    DropdownInput,
    ProgressCircle,
  },

  setup() {
    const { runSequence, interruptSequence, currentSeq } = useSequence();
    const { currentContent, hasMediaContent } = useContent();
    const { setVariables, getVariable, setFlowStateBackToStart, currentConfig, currentLanguage } = useConfig();
    const { currentAction } = useActions();
    const { amendAction, lastAnalyticData } = useAnalytics();
    const { recognizedText, sttStart, sttStop, isMicAccessGranted, sttPhrases } = useStt();
    const { disableScrollingOnCanvasTouch } = useCanvasInput();

    let keyboard;
    const isShifted = ref(false);
    const isLocked = ref(false);
    const progress = ref(0);
    let thankYouTextTimeoutId;
    let sttTimeoutId;
    let errorTimeoutId;
    let isTouched = false;
    const placeholder = useDynamicText({
      value: currentContent.value?.attrs?.placeholder
          || USER_INPUT_DEFAULT_PLACEHOLDERS[currentContent.value.attrs?.inputType],
    });
    const headlineText = useDynamicText(currentContent.value?.attrs?.headline);
    const textText = useDynamicText(currentContent.value?.attrs?.text);

    const headline = computed(() => ({
      ...headlineText.value,
      fontSize: headlineText.value.fontSize ?? 56,
    }));

    const text = computed(() => ({
      ...textText.value,
      fontSize: textText.value.fontSize ?? 36,
    }));
    const inputValue = ref('');
    const error = ref('');
    const showThankYou = ref(false);
    const maxlength = computed(() => (currentContent.value.attrs?.inputType === 'phone' ? 15 : null));
    const fakeInputValue = computed(() => {
      if (!inputValue.value.trim().length) {
        return placeholder.value.value || '<br/>';
      }

      if (inputValue.value.endsWith('\n')) {
        return `${inputValue.value}<br/>`; // add fake symbol at the end to allow user move cursor to a new line
      }

      return inputValue.value;
    });
    const inputMode = computed(() => {
      const type = currentContent.value.attrs?.inputType;

      if (type === 'phone') {
        return 'tel';
      }

      if (type === 'number') {
        return 'numeric';
      }

      return type;
    });
    const isDropdown = computed(() => inputMode.value === 'dropdown');
    const dropdownOptions = computed(() => {
      if (currentContent.value.attrs?.useDropdownVariable) {
        const { value } = getVariable(currentContent.value.attrs.dropdownVariableId) || {};
        if (value) {
          try {
            if (typeof value === 'string') {
              return JSON.parse(value);
            }
            if (Array.isArray(value)) {
              return value;
            }
            return [];
          } catch (err) {
            return [];
          }
        }

        return [];
      }

      return currentContent.value.attrs?.dropdownTable.map(({ label, value }) => ({
        label: new ShortCodeService(label)
          .matchAttributes()
          .getValue(),
        value: new ShortCodeService(value)
          .matchAttributes()
          .getValue(),
      })) || [];
    });
    const shouldShowMicButton = computed(() => inputMode.value !== 'canvas' && isMicAccessGranted());
    const showMicButton = ref(false);
    const isKeyboardOpen = ref(false);

    const inputMask = computed(() => {
      if (inputMode.value === 'time') {
        return '##:##';
      }

      if (inputMode.value === 'date') {
        return '##.##.####';
      }

      if (inputMode.value === 'timeDate') {
        return '##:## | ##.##.####';
      }

      return null;
    });

    const inputKeyboardMaskValue = computed(() => {
      if (inputMode.value === 'time') {
        return dayjs().format('HH:mm');
      }

      if (inputMode.value === 'date') {
        return dayjs().format('DD.MM.YYYY');
      }

      if (inputMode.value === 'timeDate') {
        return `${dayjs().format('HH:mm')} | ${dayjs().format('DD.MM.YYYY')}`;
      }

      return null;
    });

    const focusRef = ref(null);
    const keyboardRef = ref(null);

    function validate(value) {
      isTouched = true;
      const type = currentContent.value.attrs?.inputType;
      error.value = VALIDATIONS[type](value);
      return !error.value.length;
    }

    function restartNoInteraction() {
      if (currentAction.value.name === types.PAUSE && currentAction.value.params.reset_on_interaction) {
        currentAction.value.restart();
      }
    }

    function stopNoInteraction() {
      if (currentAction.value.name === types.PAUSE && currentAction.value.params.reset_on_interaction) {
        currentAction.value.stopTimeout();
      }
    }

    function handleInput() {
      sttStop();
      restartNoInteraction();

      // handle pressing enter with no text
      if (!inputValue.value.trim().length) {
        inputValue.value = '';
      }

      if (isTouched) {
        validate(inputValue.value);
      }
    }

    function handleNext() {
      if (showThankYou.value) {
        return;
      }

      if (!inputValue.value.trim().length) {
        return;
      }

      if (!validate(inputValue.value)) {
        return;
      }

      const value = isDropdown.value
        ? dropdownOptions.value[parseInt(inputValue.value, 10)].value
        : inputValue.value;

      const type = currentContent.value.attrs?.inputType;
      amendAction({
        userInputText: type === 'text' ? inputValue.value : undefined,
        userInputPhone: type === 'phone' ? inputValue.value : undefined,
        userInputNumber: type === 'number' ? inputValue.value : undefined,
        userInputEmail: type === 'email' ? inputValue.value : undefined,
        userInputTime: type === 'time' ? inputValue.value : undefined,
        userInputDate: type === 'date' ? inputValue.value : undefined,
        userInputTimeDate: type === 'timeDate' ? inputValue.value : undefined,
        useInputCanvas: type === 'canvas' ? inputValue.value : undefined,
      });

      error.value = '';
      showThankYou.value = true;
      if (currentContent.value.attrs?.variableId) {
        setVariables([{
          id: currentContent.value.attrs.variableId,
          value: { value, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
        }]);
      }
      if (thankYouTextTimeoutId) {
        clearTimeout(thankYouTextTimeoutId);
      }
      thankYouTextTimeoutId = setTimeout(() => {
        if (!currentContent.value.attrs?.next) {
          return;
        }
        interruptSequence(false);
        setFlowStateBackToStart(currentContent.value.attrs.next);
        runSequence(currentContent.value.attrs.next);
      }, !currentContent.value.attrs.endText.value ? 0 : INTERACTION_DELAY);
    }

    function activateStt() {
      if (isMicAccessGranted()) {
        showMicButton.value = false;
        sttStart({
          sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
          elementId: lastAnalyticData.value.elementId,
        });
        clearTimeout(errorTimeoutId);
        error.value = '';
      }
    }

    function handleClear() {
      inputValue.value = '';
      clearInterval(sttTimeoutId);
      progress.value = 0;
      if (currentAction.value.name === types.PAUSE && currentAction.value.params.reset_on_interaction) {
        currentAction.value.restart();
      }
      if (shouldShowMicButton.value) {
        activateStt();
      }
    }

    watch(recognizedText, async (value) => {
      const speechText = value.text.trim();
      if (value.isFinal && speechText) {
        if (isDropdown.value) {
          restartNoInteraction();
          const optionIndex = dropdownOptions.value
            .findIndex((o) => speechText.toLowerCase().includes(o.label?.toLowerCase()));
          if (optionIndex !== -1) {
            inputValue.value = optionIndex.toString();
          } else {
            error.value = 'Sorry, no match';
            errorTimeoutId = setTimeout(() => {
              error.value = '';
            }, 2000);
          }
        } else {
          inputValue.value = speechText;
          restartNoInteraction();
        }
        sttStop();
        if (shouldShowMicButton.value) {
          showMicButton.value = true;
        }
      }
    });

    function handleMaskedInput(evt) {
      inputValue.value = evt.detail.masked;
      handleInput();
    }

    function handleBackspace() {
      if (keyboard) {
        keyboard.setInput(inputValue.value.slice(0, -1));
      }
    }

    function onCanvasData(data) {
      inputValue.value = data;
    }

    function onDropdownData(data) {
      inputValue.value = data;
    }

    function handleShift() {
      const currentLayout = keyboard.options.layoutName;
      const shiftToggle = currentLayout === 'default' ? 'shift' : 'default';

      keyboard.setOptions({
        layoutName: shiftToggle,
      });
    }

    async function onChange(input) {
      const position = keyboard.getCaretPosition();
      inputValue.value = input;
      handleInput();

      if (isShifted.value) {
        isShifted.value = false;
        handleShift();
      }

      if (position) {
        await nextTick();
        focusRef.value.setSelectionRange(position, position);
      }
    }

    function onKeyPress(button) {
      if (button.includes('{enter}')) {
        handleNext();
      }

      // user pressed lock while shift is active then just cancel shift
      if (button === '{lock}' && isShifted.value && !isLocked.value) {
        isShifted.value = false;
        isLocked.value = true;
        return;
      }

      if (button === '{shift}' || button === '{lock}') {
        isShifted.value = button === '{shift}';
        if (button === '{lock}') {
          isLocked.value = !isLocked.value;
        }
        handleShift();
      }
    }

    function handleClick() {
      if (userAgent.phone || !currentConfig.value.customPayload.useVirtualKeyboard) {
        return;
      }

      if (keyboard || isKeyboardOpen.value) {
        return;
      }

      const currentLangCode = currentConfig.value.contentLanguages
        .find((l) => l.code === currentLanguage.value).iso639_1;

      const loadKeyboard = async () => {
        let layoutConfig = {};

        if (KEYBOARD_LAYOUTS[currentLangCode]) {
          try {
            const layoutModule = await KEYBOARD_LAYOUTS[currentLangCode]();
            layoutConfig = layoutModule.default;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn(`Failed to load keyboard layout for ${currentLangCode}`, err);
          }
        }

        keyboard = new Keyboard('keyboard', {
          preventMouseDownDefault: true,
          modules: inputMask.value ? [keyboardInputMask] : [],
          inputMaskPhysicalKeyboardHandling: true,
          syncInstanceInputs: true,
          inputMaskTargetClass: 'input',
          inputMask: {
            default: {
              mask: inputKeyboardMaskValue.value,
              regex: /^[0-9]*$/,
            },
          },
          disableCaretPositioning: !!inputMask.value,
          onChange: (input) => onChange(input),
          onKeyPress: (button) => onKeyPress(button),
          ...layoutConfig,
        });

        keyboard.setInput(inputValue.value);
      };

      // Set flag immediately before async operation
      isKeyboardOpen.value = true;
      loadKeyboard();
    }

    function handleBlur() {
      if (keyboard) {
        keyboard.destroy();
        keyboard = null;
      }
      isKeyboardOpen.value = false;
    }

    onMounted(() => {
      setVariables([{
        id: SYSTEM_VARIABLES_MAP.CURRENT_DESIGN_ELEMENT.id,
        value: { value: currentSeq.value.id },
      }]);
      if (inputMode.value === 'canvas') {
        const userInputDiv = document.getElementsByClassName('user-input')[0];
        disableScrollingOnCanvasTouch(userInputDiv);
        return;
      }

      if (isDropdown.value) {
        sttPhrases([{
          variations: dropdownOptions.value.map(({ label }) => label),
        }]);
      }
      if (shouldShowMicButton.value) {
        activateStt();
      }

      if (focusRef.value) {
        focusRef.value.focus();
      }
    });

    // user switch between tabs or pressed restart button
    onBeforeUnmount(() => {
      sttStop();
      clearInterval(sttTimeoutId);
      clearTimeout(thankYouTextTimeoutId);
    });

    return {
      progress,
      currentContent,
      inputValue,
      inputMask,
      fakeInputValue,
      error,
      showThankYou,
      inputMode,
      maxlength,
      headline,
      text,
      placeholder,
      showMicButton,
      isDropdown,
      dropdownOptions,
      keyboardRef,
      handleInput,
      handleNext,
      handleClear,
      handleMaskedInput,
      handleClick,
      handleBlur,
      activateStt,
      isMicAccessGranted,
      hasMediaContent,
      onCanvasData,
      onDropdownData,
      focusRef,
      restartNoInteraction,
      stopNoInteraction,
      handleBackspace,
      isShifted,
      isLocked,
      isKeyboardOpen,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/common-ui.scss";

.user-input {
  @extend .content-container;

  &.keyboard-open {
    // Make space for the virtual keyboard positioned above the input
    // and allow it to overflow the container bounds without clipping.
    padding-top: clamp(140px, 28vmin, 280px);
    overflow: visible;
  }
}

.progress-circle {
  width: clamp(38px, 6vmin, 58px);
  height: clamp(38px, 6vmin, 58px);
  position: relative;
}

.keyboard-wrapper {
  position: relative;

  &.shifted {
    :deep(.hg-button-shift) {
      color: #007CBA;
    }
  }

  &.locked {
    :deep(.hg-button-lock) {
      color: #007CBA;
    }
  }
}

.keyboard {
  z-index: 100;
  position: absolute;
  bottom: calc(100% + 32px);
  left: 50%;
  transform: translateX(-50%);
  width: clamp(300px, 95vw, 800px);
  max-width: 100%;
}

.input-block {
  width: 90%;
  background-color: #fff;
  border: 1px solid #E1E6EC;
  box-shadow: 0 2px 16px rgba(141, 151, 159, 0.1);
  z-index: 1;
  border-radius: clamp(8px, 2vmin, 16px);
  margin: clamp(12px, 3vmin, 32px) auto;
  padding: clamp(10px, 2vmin, 30px) clamp(12px, 3vmin, 32px);
}

.input-wrapper {
  max-height: 40vmin;
  overflow: auto;

  .input-holder {
    position: relative;
    width: 100%;
    float: left;
    margin-top: 8px;

    &.empty {
      width: calc(100% - 136px);
    }

    .fake-input {
      text-align: left;
      opacity: 0;
      font-family: 'Roboto', Avenir, Helvetica, Arial, sans-serif;
      white-space: pre-wrap;
      overflow-wrap: break-word;
      font-size: clamp(14px, 3vmin, 28px);
      line-height: 1.4;
    }
  }

  .input-icons {
    display: flex;
    justify-content: flex-end;
    width: 126px;
    float: right;
  }

  .mic-icon {
    margin-right: 10px;
  }
}

.icon {
  position: relative;
  cursor: pointer;
  border: 2px solid transparent;
  flex-shrink: 0;
  border-radius: 100%;
  height: clamp(38px, 6vmin, 58px);
  width: clamp(38px, 6vmin, 58px);

  &:hover:not(.disabled) {
    opacity: 0.9;
  }

  &.disabled {
    opacity: 0.5;
  }

  img {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: auto;
  }
}

.next-icon {
  background-color: #007CBA;
  border: none;
}

.clear-icon, .mic-icon {
  border-color: #E1E6EC;
}

.input {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  padding: 0;
  resize: none;
  border: none;
  outline: none;
  font-family: 'Roboto', Avenir, Helvetica, Arial, sans-serif;
  letter-spacing: 0.15px;
  overflow: hidden;
  font-size: clamp(16px, 3vmin, 28px);
  line-height: 1.4;
}

.controls {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: clamp(12px, 2vmin, 16px);
  padding-top: clamp(12px, 2vmin, 16px);
  border-top: 1px solid #E1E6EC;

  &.no-borders {
    border-top: none;
    margin-top: 0;
    padding-top: clamp(12px, 2vmin, 15px);
  }

  .margin-left {
    margin-left: clamp(8px, 1.5vmin, 10px);
  }
}

.errors {
  flex-grow: 2;
  margin-right: 16px;
  text-align: left;
  color: #FF1900;
  letter-spacing: 0.15px;

  font-size: clamp(15px, 2vmin, 16px);
  line-height: 1.5;
}
</style>
